import React, {
  useEffect, useState, useRef, useCallback,
} from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import {
  EVSnackbar, EVAlert, withStyles,
} from '@eagleview/ev-comp-library';
import Interceptor from 'fetchInterceptor';
import microfrontend from 'utils/microfrontendUtil';
import {
  getNewAccessTokenAction, closeContainerToastMessage, openContainerToastMessage, resetTimerAction, getCustomAccessToken, setShowReloadDialog,
} from 'features/authorize/Authorize.actions';
import {
  navigateAction, navigationBlockerAction, navigationUnBlockerAction, setFeatureFlagLoaded,
} from 'features/side-bar/SideBar.actions';
import {
  getTokenExpiryTime, getTimeoutTime, getTimeoutWindowMinutes, initializeRollbar, getAuthToken,
} from 'utils/app.utils';
import logOut from 'utils/logout.utils';
import {
  REFRESH_CHECK_INTERVAL, JS_ERROR, TRANSLATION_ERROR_LIST, USER_IMPERSONATION_BROADCAST_TYPE, BANNER_POSITION, SIGN_IN_AS_GUID,
} from 'constants/AppConstants';
import Shimmer from 'commonComponents/Loader/Shimmer.component';
import SignoutBannerComponent from 'commonComponents/SignOutBanner/SignoutBanner.component';
import { setFullscreen } from 'utils/mfa.utils';
import Events from 'event';
import clsx from 'clsx';
import useUserImpersonationEventListener from 'hooks/useUserImpersonationEventListener';
import Cookies from 'js-cookie';
import useSysendBroadcastListener from 'hooks/useSysendBroadcastListener';
// #genai guest-access context callback for child micro-frontends
import { useGuestContextCallback } from 'features/guest-access';
import { updateResourceAccessAuditStatusService } from 'services/GuestAccess.service';
import ImpersonationReloadDialog from './components/ImpersonationReloadDialog.component';
import DisplayError from './components/DisplayError.component';

const mfInstance = microfrontend.getInstance();

// The --mobileHeight css variable allows an app to set the height of the root div in a way that works with mobile browsers.
// This is necessary because the 100vh setting does not factor in the url bar and tool bar that most mobile devices use which can cause some
// elements to be cut off. Setting the --mobileHeight variable to visualViewport.height, window.innerHeight, or client height can help solve this problem.
const styles = (theme) => ({
  contentWidth: {
    maxWidth: theme.spacing(85),
    marginLeft: '40px',
    marginTop: '40px',
    fontSize: 'initial',
  },
  microAppContainerNoHeader: {
    position: 'relative',
    height: 'var(--mobileHeight, 100vh)',
    overflow: 'auto',
  },
  microAppContainer: {
    position: 'relative',
    height: 'calc(var(--mobileHeight, 100vh) - 44px)',
    overflow: 'auto',
  },
  microAppContainerWithOldSidebar: {
    width: 'calc(100vw - 63px)', // #genai matches PrimaryNavBarOld drawerWidthClose
  },
  microAppContainerWithNewSidebar: {
    width: 'calc(100vw - 60px)',
  },
  microAppContainerNoSidebar: {
    width: '100vw',
  },
});

/**
 * MfContainer Component - loads and mounts selected micro application
 */
const MfContainer = (props) => {
  const { t } = useTranslation();
  const {
    mfaList,
    defaultApp: { appId: defaultAppId },
    mfaResponseReceived,
    authorize,
    startTimer,
    showToast,
    retryCount,
    errorCode,
    errorDescription,
    classes,
    flags,
    ldClient,
    userDetails,
    loaders,
    navigateTo,
    featureFlagsLoaded,
    showNewUI,
    shouldHeaderRender,
    shouldSidebarRender,
    isImpersonated,
    showReloadDialog,
    isSignOutDisabled,
    userProfileError,
    isShareToken,
    sharedStartup,
  } = props;
  const [error, setError] = useState({ status: false, message: '' });
  const [isLoading, setIsLoading] = useState(true);
  /**
   * extracts parameter from url
   */
  const params = useParams();
  /**
   * provides access to history object
   */
  const history = useHistory();
  const dispatch = useDispatch();
  const isVpNavigate = useRef(false);
  const currentApp = useRef('');
  useUserImpersonationEventListener((impersonationGuid) => dispatch(getCustomAccessToken(impersonationGuid)));
  useSysendBroadcastListener(USER_IMPERSONATION_BROADCAST_TYPE, () => dispatch(setShowReloadDialog(true)));
  // Stable callback child micro-frontends can call to read the current guest context.
  // Always returns the latest Redux GuestAccess state even after initial mount.
  const getGuestContext = useGuestContextCallback();

  // #genai true when a shared-resource appUrl is set for direct mount
  // (non-EV guest, or EV user whose app is not in org entitlements)
  const isGuestDirectMount = sharedStartup.shared && !!sharedStartup.appUrl;
  const auditDeniedSentRef = useRef(false);

  const checkAppAccess = () => {
    const { appName } = params;
    if (isEmpty(appName)) {
      return true;
    }
    if (isGuestDirectMount && sharedStartup.appCode === appName) return true;
    return mfaList.some(({ appId }) => appId === appName);
  };

  const prepareFullScreenStatus = () => ({
    sidebarVisible: shouldSidebarRender,
    headerVisible: shouldHeaderRender,
    fullscreen: !shouldHeaderRender && (!shouldSidebarRender || mfaList.length === 1),
  });

  const fullScreenStatus = useRef(prepareFullScreenStatus());

  useEffect(() => {
    fullScreenStatus.current = prepareFullScreenStatus();
  }, [shouldSidebarRender, shouldHeaderRender]);

  /**
   * clickActionBlockerCallback called from layer admin app
   */
  const navigationBlockerCallback = () => {
    dispatch(navigationBlockerAction());
  };

  /**
   * saveDontSaveClickedCallback called from layer admin app
   */
  const navigationUnBlockerCallback = () => {
    dispatch(navigationUnBlockerAction());
  };
  /**
   * Handle error
   */
  const handleError = (err) => {
    const { appName } = params;
    const urlParams = new URLSearchParams(window.location.search);
    setIsLoading(false);
    if (appName === 'cloudexplorer' && urlParams.size > 0) {
      setError({ status: true, message: 'Invalid shared link URL. Please check the link and try again or contact the sender for assistance' });
      return;
    }
    let errorMessage = err.message || err || '';
    if (typeof errorMessage === 'object') {
      errorMessage = JSON.stringify(errorMessage);
    }
    if (errorMessage === t('UnableToOpen')) {
      setError({ status: true, message: errorMessage });
    } else {
      setError({ status: true, message: `Unable to open ${errorMessage}` });
    }
  };

  useEffect(() => {
    if (isLoading) {
      setError({ status: false, message: '' });
    }
  }, [isLoading]);

  const getFullscreenInfo = () => fullScreenStatus.current;

  // #genai shared mount helper — used by both guest-direct-mount and entitlement-based paths.
  const mountApp = (appName, url) => {
    // eslint-disable-next-line no-console
    console.log('[MfContainer] mountApp — opening microfrontend container', { appName, url });
    setIsLoading(true);
    initializeRollbar(appName);
    Events.getEventInstance().removeAllListeners();
    mfInstance.mount({
      name: appName,
      url,
      props: {
        history,
        fetchIntercept: Interceptor().fetchIntercept,
        featureFlag: flags,
        sharedStartup,
        navigationBlocker: navigationBlockerCallback,
        navigationUnBlocker: navigationUnBlockerCallback,
        setFullscreen: (args) => setFullscreen(dispatch, args),
        subscribe: (eventName, callback) => Events.getEventInstance().on(eventName, callback),
        unSubscribe: (eventName, callback) => Events.getEventInstance().off(eventName, callback),
        getFullscreenInfo,
        getGuestContext,
        logOut,
      },
      elementId: 'mfa',
    })
      .then(() => {
        // eslint-disable-next-line no-console
        console.log('[MfContainer] mountApp — microfrontend container mounted successfully', { appName });
        setIsLoading(false);
        if (error.status) setError({ status: false, message: '' });
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.log('[MfContainer] mountApp — failed to mount microfrontend container', { appName, error: err });
        handleError(err);
      });
  };

  /**
   * loads the selected micro application by calling micro-frontend-utils
   */
  const loadMF = () => {
    // #genai Direct mount from SharedStartup.appUrl takes priority — handles both
    // non-EV guests and EV users whose shared app is not in org entitlements.
    // Checked first so an EV user's non-empty mfaList doesn't intercept the mount.
    if (isGuestDirectMount) {
      const appName = params.appName || sharedStartup.appCode;
      currentApp.current = appName;
      const isAppMounted = mfInstance.mountedList.find((app) => app.name === appName) !== undefined;

      if (!isAppMounted) {
        document.title = sharedStartup.appName;
        mountApp(appName, sharedStartup.appUrl);
      }
    } else if (mfaList && mfaList.length && checkAppAccess()) {
      const { appName = defaultAppId } = params;
      currentApp.current = appName;
      const mfApp = mfaList.filter((mfaDetails) => mfaDetails.appId === appName);
      const { rootUrl = '', name = '' } = (Array.isArray(mfApp) && mfApp[0]) || {};
      const isAppMounted = mfInstance.mountedList.find((app) => app.name === appName) !== undefined;

      if (!isAppMounted || isVpNavigate.current) {
        isVpNavigate.current = false;
        dispatch(navigateAction({ navigateTo: '' }));
        document.title = name;
        mountApp(appName, rootUrl);
      }
    } else {
      setIsLoading(false);
    }
  };

  /**
   * prefetch micro application by calling micro-frontend-utils
   */
  const prefetch = (mfa) => {
    const microfrontendInstamnce = microfrontend.getInstance();

    mfa.forEach((mfaDetails) => {
      if (mfaDetails.prefetch) {
        microfrontendInstamnce.prefetchMF({
          name: mfaDetails.appId,
          url: mfaDetails.rootUrl,
        });
      }
    });
    setError({ status: false, message: '' });
  };

  const MfAppAccess = () => {
    let errorMsg;
    let errorDesc;
    let showSupportLink = false;

    // Guest (SHARE token) on a non-shared-resource URL — show only for non-EV users. #genai
    if (!sharedStartup.shared && isShareToken && !sharedStartup.isEVUser && (!mfaList || mfaList.length === 0)) {
      errorMsg = 'GuestNoResourceAccess';
      errorDesc = '';
      showSupportLink = false;
    } else if (userProfileError) {
      errorMsg = 'EUMUserProfileError';
      errorDesc = '';
      showSupportLink = true;
    } else if (mfaResponseReceived && (!mfaList || mfaList.length === 0) && !isGuestDirectMount) {
      errorMsg = 'NoApps';
      errorDesc = 'NoAppsReachUsOut';
      showSupportLink = true;
    } else if (mfaResponseReceived && !checkAppAccess()) {
      const { appName } = params;
      if (appName === 'OC') {
        errorMsg = 'NotEntitledInternal';
        errorDesc = '';
      } else {
        errorMsg = 'NotEntitled';
        errorDesc = '';
      }

      if (sharedStartup?.shared && sharedStartup?.resourceAccessAuditId && !auditDeniedSentRef.current) {
        auditDeniedSentRef.current = true;
        updateResourceAccessAuditStatusService(
          sharedStartup.resourceAccessAuditId,
          'DENIED',
          getAuthToken(),
        ).catch(() => {});
      }
    }

    if (errorMsg) {
      return (
        <DisplayError
          errorMsg={errorMsg}
          errorDesc={errorDesc}
          showSupportLink={showSupportLink}
          contentWidth={classes.contentWidth}
        />
      );
    }

    return <></>;
  };

  /**
   * Checks for the token expiry at regular intervals and dispatches action to get new token when it is about to expire
   */
  const setIntervalFunc = () => {
    let timer = 0;
    const stopIntervalFunc = () => {
      if (timer) {
        clearTimeout(timer);
        timer = 0;
      }
    };
    const startIntervalFunc = () => {
      const currTime = new Date();
      const expiryTime = new Date(getTokenExpiryTime());
      if (currTime < expiryTime && currTime >= new Date(expiryTime.getTime() - (getTimeoutWindowMinutes() * 60 * 1000))) {
        stopIntervalFunc();
        dispatch(resetTimerAction(false));
        const timeoutTime = getTimeoutTime(expiryTime);
        setTimeout(() => dispatch(getNewAccessTokenAction(false, '')), timeoutTime);
      } else if (currTime > expiryTime) {
        stopIntervalFunc();
        dispatch(resetTimerAction(false));
      } else {
        timer = setTimeout(startIntervalFunc, REFRESH_CHECK_INTERVAL);
      }
    };
    timer = setTimeout(startIntervalFunc);
  };

  useEffect(() => {
    if (ldClient && userDetails.userName) {
      if (!featureFlagsLoaded) {
        const user = {
          key: userDetails.userName,
          kind: 'user',
          organizationID: userDetails.organization.id,
        };
        ldClient.identify(user, null, () => {
          dispatch(setFeatureFlagLoaded(true));
        });
      }
    }
  }, [userDetails]);

  /**
   * called when authorize and startTimer are true
   * calls setIntervalFunc function
   */
  useEffect(() => {
    if (authorize && startTimer) {
      setIntervalFunc();
    }
  }, [authorize, startTimer]);

  /**
   * called when micro-frontend application list changes,
   * calls prefetch function
   */
  useEffect(() => {
    prefetch(mfaList);
  }, [mfaList]);

  /**
   * called when selected application changes,
   * calls loadMF function.
   */
  useEffect(() => {
    const { appName } = params;
    if (!featureFlagsLoaded || (appName === currentApp.current && !isVpNavigate.current)) return;
    loadMF();
  }, [mfaList, params, featureFlagsLoaded, sharedStartup.appUrl]);

  useEffect(() => {
    if (!isEmpty(navigateTo)) {
      history.push(navigateTo);
      isVpNavigate.current = true;
      dispatch(navigateAction({ navigateTo: '' }));
    }
  }, [navigateTo]);
  /**
   * catch global JS errors and log
   */
  useEffect(() => {
    // catch uncaught JS error
    window.onerror = (msg, url, line, column, err) => {
      dispatch(openContainerToastMessage());
      if (window.ev_vp && window.ev_vp.Logger) {
        const stack = err && err.stack ? err.stack : JSON.stringify(err);
        const message = `Message: ${msg} URL: ${url}  Line: ${line} Column: ${column} Error: ${stack}`;
        window.ev_vp.Logger.error(message, {
          msg, url, line, column, err,
        });
      }
      return true;
    };
  }, []);

  /**
   * Method triggered to close the toast
   */
  const handleToastClose = () => {
    dispatch(closeContainerToastMessage());
  };

  const loader = loaders[params.appName];

  const getError = () => {
    if ([...TRANSLATION_ERROR_LIST, JS_ERROR].includes(errorDescription)) {
      return errorDescription === JS_ERROR ? t(JS_ERROR) : t(errorDescription);
    }

    const errCode = errorCode ? `${t('ErrorCode')}: ${errorCode}` : '';
    return `${t('FailedAuthentication')} ${errCode} ${errorDescription || ''}.${t('Retrying')} ${retryCount} ${t('RetryCount')}`;
  };

  const [bounds, setBounds] = useState({
    left: 0, top: 0, right: 0, bottom: 0,
  });
  const [position, setPosition] = useState({ x: window.innerWidth - 600, y: 0 });
  const divRef = useRef(null);
  const isDraggedRef = useRef(false);
  const positionRef = useRef({ x: window.innerWidth - 600, y: 0 });

  useEffect(() => {
    const savedPos = Cookies.get(BANNER_POSITION);
    if (savedPos) {
      const parsedPos = JSON.parse(savedPos);
      setPosition(parsedPos);
      positionRef.current = parsedPos;
    }
  }, []);

  const handleResize = (entries) => {
    // eslint-disable-next-line no-restricted-syntax
    for (const entry of entries) {
      if (entry.target === divRef.current) {
        setBounds({
          left: 0,
          top: 0,
          right: entry.contentRect.width - 400, // width of the divRef minus the width of floating panel
          bottom: entry.contentRect.height - 30, // height of the divRef minus the height of floating panel
        });

        const savedPos = Cookies.get(BANNER_POSITION);
        if (!isDraggedRef.current && savedPos) {
          const parsedPos = JSON.parse(savedPos);
          setPosition(parsedPos);
          positionRef.current = parsedPos;
        } else {
          let newX = positionRef.current.x;
          let newY = positionRef.current.y;

          // Check boundaries
          if (newX + 400 > entry.contentRect.width) {
            newX = entry.contentRect.width - 400; // Adjust if it goes beyond right edge
          }
          if (newX < 0) {
            newX = 0; // Adjust if it goes beyond left edge
          }
          if (newY + 30 > entry.contentRect.height) {
            newY = entry.contentRect.height - 30; // Adjust if it goes beyond bottom edge
          }
          if (newY < 0) {
            newY = 0; // Adjust if it goes beyond top edge
          }
          setPosition({ x: newX, y: newY });
          positionRef.current = { x: newX, y: newY };
        }
      }
    }
  };

  useEffect(() => {
    const resizeObserver = new ResizeObserver(handleResize);
    if (divRef.current) {
      resizeObserver.observe(divRef.current);
    }

    return () => {
      if (divRef.current) {
        resizeObserver.unobserve(divRef.current);
      }
      resizeObserver.disconnect();
    };
  }, []);

  const handleDrag = useCallback((e, data) => {
    isDraggedRef.current = true;
    setPosition({ x: data.x, y: data.y });
    positionRef.current = { x: data.x, y: data.y };
    Cookies.set(BANNER_POSITION, JSON.stringify({ x: data.x, y: data.y }), { expires: 7 });
  }, []);

  const handleReload = () => {
    dispatch(setShowReloadDialog(false));
    if (localStorage.getItem(SIGN_IN_AS_GUID)) {
      window.location.assign('/');
    } else {
      window.location.reload();
    }
  };

  return (
    <>

      {userProfileError ? (
        <MfAppAccess userProfileError={userProfileError} />
      ) : (
        <div ref={divRef}>
          {isImpersonated && !showReloadDialog && (
          <SignoutBannerComponent
            userDetails={userDetails}
            bounds={bounds}
            position={position}
            handleDrag={handleDrag}
            isSignOutDisabled={isSignOutDisabled}
          />
          )}
          <MfAppAccess />
          {isLoading && (mfaResponseReceived && (mfaList?.length !== 0 || isGuestDirectMount)) && (
            <Shimmer content={loader} appName={params.appName} />
          )}
          {
            (mfaList?.length !== 0 || isGuestDirectMount)
            && (
              <div
                id="mfa"
                className={
                  clsx(
                    { [classes.microAppContainer]: showNewUI && shouldHeaderRender },
                    { [classes.microAppContainerNoHeader]: !(showNewUI && shouldHeaderRender) },
                    { [classes.microAppContainerNoSidebar]: !shouldSidebarRender || (showNewUI && mfaList?.length === 1) },
                    { [classes.microAppContainerWithOldSidebar]: !showNewUI && shouldSidebarRender },
                    { [classes.microAppContainerWithNewSidebar]: showNewUI && shouldSidebarRender },
                  )
                }
              />
            )
          }
        </div>
      )}
      <EVSnackbar
        id="containerToast"
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={showToast}
        onClose={handleToastClose}
        autoHideDuration={3000}
      >
        <EVAlert severity="error">
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: getError(),
            }}
          />
        </EVAlert>
      </EVSnackbar>
      {error.status
        && (
          <EVSnackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            open={error.status}
            onClose={handleToastClose}
            autoHideDuration={3000}
          >
            <EVAlert severity="error">
              <div
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: error.message,
                }}
              />
            </EVAlert>
          </EVSnackbar>
        )}
      {showReloadDialog && (
        <ImpersonationReloadDialog openDialog={showReloadDialog} handleReload={handleReload} />
      )}
    </>
  );
};

// #genai Map common GuestAccess state to sharedStartup shape expected by MfContainer
const getSharedStartupFromState = (state) => {
  const g = state.GuestAccess || {};
  return {
    sharedPhase: g.sharedPhase,
    shared: g.isResourceShared,
    appCode: g.appCode,
    appUrl: g.appUrl,
    appName: g.appName,
    resourcePath: g.resourcePath,
    resourceShareId: g.resourceShareId,
    isEVUser: g.isEvUser,
    hasAccess: g.hasAccess,
    resourceAccessAuditId: g.resourceAccessAuditId,
  };
};

const selector = (state) => ({
  mfaList: state.SideBar.mfaConfig,
  defaultApp: state.SideBar.defaultApp,
  mfaResponseReceived: state.SideBar.mfaResponseReceived,
  authorize: state.Authorize.authorize,
  userDetails: state.SideBar.userDetails,
  startTimer: state.Authorize.startTimer,
  showToast: state.Authorize.showToast,
  retryCount: state.Authorize.retryCount,
  errorCode: state.Authorize.errorCode,
  errorDescription: state.Authorize.errorDescription,
  loaders: state.SideBar.loaders,
  navigateTo: state.SideBar.navigateTo,
  featureFlagsLoaded: state.SideBar.featureFlagsLoaded,
  showNewUI: state.SideBar.showNewUI,
  shouldHeaderRender: state.SideBar.shouldHeaderRender,
  shouldSidebarRender: state.SideBar.shouldSidebarRender,
  isImpersonated: state.SideBar.isImpersonated,
  showReloadDialog: state.Authorize.showReloadDialog,
  isSignOutDisabled: state.Authorize.isSignOutDisabled,
  userProfileError: state.SideBar.userProfileError,
  isShareToken: Boolean(state.Authorize?.isShareToken),
  sharedStartup: getSharedStartupFromState(state),
});

MfContainer.propTypes = {
  /**
   * The list of micro applications.
   * @param {{appId: String, rootUrl: String, prefetch: Boolean, name: String}[]} mfaList
   */
  mfaList: PropTypes.arrayOf(
    PropTypes.shape({
      appId: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      rootUrl: PropTypes.string.isRequired,
      prefetch: PropTypes.bool,
    }),
  ),

  /**
   * details of default micro application to load applications.
   * @param {{appId: String, rootUrl: String, prefetch: Boolean, name: String}} defaultApp
   */
  defaultApp: PropTypes.shape({
    appId: PropTypes.string.isRequired,
    name: PropTypes.string,
    rootUrl: PropTypes.string,
    prefetch: PropTypes.bool,
  }),
  flags: PropTypes.shape({}).isRequired,
  ldClient: PropTypes.shape({
    identify: PropTypes.func.isRequired,
    allFlags: PropTypes.func.isRequired,
  }).isRequired,
  mfaResponseReceived: PropTypes.bool.isRequired,
  userDetails: PropTypes.shape({
    userName: PropTypes.string.isRequired,
    organization: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
  authorize: PropTypes.bool,
  startTimer: PropTypes.bool,
  showToast: PropTypes.bool,
  featureFlagsLoaded: PropTypes.bool.isRequired,
  retryCount: PropTypes.number,
  errorCode: PropTypes.number,
  errorDescription: PropTypes.string,
  navigateTo: PropTypes.string.isRequired,
  classes: PropTypes.shape({
    contentWidth: PropTypes.string,
    centeredErrorContainer: PropTypes.string,
    centeredErrorContent: PropTypes.string,
    microAppContainer: PropTypes.string,
    microAppContainerNoHeader: PropTypes.string,
    microAppContainerNoSidebar: PropTypes.string,
    microAppContainerWithOldSidebar: PropTypes.string,
    microAppContainerWithNewSidebar: PropTypes.string,
  }).isRequired,
  loaders: PropTypes.shape({
    default: PropTypes.string,
  }),
  showNewUI: PropTypes.bool.isRequired,
  shouldHeaderRender: PropTypes.shape().isRequired,
  shouldSidebarRender: PropTypes.shape().isRequired,
  isImpersonated: PropTypes.bool,
  showReloadDialog: PropTypes.bool.isRequired,
  isSignOutDisabled: PropTypes.bool.isRequired,
  userProfileError: PropTypes.bool,
  isShareToken: PropTypes.bool,
  sharedStartup: PropTypes.shape({
    sharedPhase: PropTypes.string,
    shared: PropTypes.bool,
    appCode: PropTypes.string,
    appUrl: PropTypes.string,
    appName: PropTypes.string,
    resourcePath: PropTypes.string,
    resourceShareId: PropTypes.string,
    email: PropTypes.string,
    isEVUser: PropTypes.bool,
    hasAccess: PropTypes.bool,
    resourceAccessAuditId: PropTypes.string,
  }).isRequired,
};
MfContainer.defaultProps = {
  mfaList: [],
  defaultApp: {
    appId: '',
  },
  userDetails: {
    organization: {
      id: '',
    },
  },
  authorize: false,
  startTimer: false,
  showToast: false,
  retryCount: 0,
  errorCode: 0,
  errorDescription: '',
  loaders: {},
  isImpersonated: false,
  userProfileError: false,
  isShareToken: false,
};
export { MfContainer };
export default withStyles(styles)(connect(selector)(withLDConsumer()(MfContainer)));
